import React, { MouseEvent, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"
import {
  bookmarkStudentQuestion,
  unbookmarkStudentQuestion,
} from "../../../api/studentQuestions"
import { getLearnerApiErrorMessage } from "../../../auth/learner/errors"
import useQuestionList from "./hooks/useQuestionList"
import useQuestionDetail from "./hooks/useQuestionDetail"
import {
  DEFAULT_QUESTION_LIST_FILTER,
  type DetailTab,
  type QuestionListFilter,
} from "./types"
import QuestionsFilterPanel from "./components/QuestionsFilterPanel"
import QuestionList from "./components/QuestionList"
import QuestionDetailPanel from "./components/QuestionDetailPanel"
import {
  mapDetailToListItem,
  pinQuestionToTop,
} from "./utils/mapQuestionCard"
import "./QuestionsPage.style.scss"

const isDesktopFilters = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(min-width: 768px)").matches

const readQuestionQuery = (params: URLSearchParams) =>
  (params.get("q") || "").trim()

const QuestionsPage = () => {
  const [params, setParams] = useSearchParams()
  const qParam = readQuestionQuery(params)
  const skipQuerySync = useRef(false)
  const [showFilters, setShowFilters] = useState(true)
  const [draftFilter, setDraftFilter] = useState<QuestionListFilter>(() => ({
    ...DEFAULT_QUESTION_LIST_FILTER,
    keyword: qParam,
  }))
  const [appliedFilter, setAppliedFilter] = useState<QuestionListFilter>(() => ({
    ...DEFAULT_QUESTION_LIST_FILTER,
    keyword: qParam,
  }))
  const [keywordInput, setKeywordInput] = useState(qParam)
  const openTabRef = useRef<DetailTab>("attempts")

  const list = useQuestionList(appliedFilter)
  const detail = useQuestionDetail()
  const detailId = params.get("id")
  const detailOpen = !!detailId
  const pinnedItem = useMemo(() => {
    if (!detailId || !detail.detail || detail.detail.id !== detailId) return null
    return mapDetailToListItem(detail.detail)
  }, [detailId, detail.detail])
  const displayItems = useMemo(
    () => pinQuestionToTop(list.items, detailId, pinnedItem),
    [list.items, detailId, pinnedItem]
  )

  // Header global search lands here via ?q=
  useEffect(() => {
    if (skipQuerySync.current) {
      skipQuerySync.current = false
      return
    }
    setKeywordInput(qParam)
    setAppliedFilter((prev) => {
      if (prev.keyword === qParam) return prev
      return { ...prev, keyword: qParam, page_number: 1 }
    })
    setDraftFilter((prev) =>
      prev.keyword === qParam ? prev : { ...prev, keyword: qParam }
    )
  }, [qParam])

  // Debounced keyword search — resets to page 1 and keeps ?q= in sync
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const keyword = keywordInput.trim()
      setAppliedFilter((prev) => {
        if (prev.keyword === keyword) return prev
        return { ...prev, keyword, page_number: 1 }
      })
      setDraftFilter((prev) =>
        prev.keyword === keyword ? prev : { ...prev, keyword }
      )
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const current = (next.get("q") || "").trim()
          if (current === keyword) return prev
          skipQuerySync.current = true
          if (keyword) next.set("q", keyword)
          else next.delete("q")
          return next
        },
        { replace: true }
      )
    }, 350)
    return () => window.clearTimeout(timer)
  }, [keywordInput, setParams])

  // Single source of truth: ?id= opens/loads detail; clearing it closes.
  useEffect(() => {
    if (!detailId) {
      detail.close()
      return
    }
    setShowFilters(false)
    detail.open(detailId, openTabRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailId])

  const handleDraftChange = (next: QuestionListFilter) => {
    setDraftFilter(next)
    if (isDesktopFilters()) {
      setAppliedFilter({
        ...next,
        keyword: keywordInput.trim(),
        page_number: 1,
      })
    }
  }

  const applyFilters = () => {
    const next = {
      ...draftFilter,
      keyword: keywordInput.trim(),
      page_number: 1,
    }
    setDraftFilter(next)
    setAppliedFilter(next)
    setShowFilters(false)
  }

  const clearFilters = () => {
    const cleared = {
      ...DEFAULT_QUESTION_LIST_FILTER,
      keyword: keywordInput.trim(),
    }
    setDraftFilter(cleared)
    setAppliedFilter(cleared)
    if (!isDesktopFilters()) setShowFilters(false)
  }

  const handlePageChange = (page: number) => {
    setAppliedFilter((prev) => ({ ...prev, page_number: page }))
    setDraftFilter((prev) => ({ ...prev, page_number: page }))
  }

  const openDetail = (id: string, tab: DetailTab = "attempts") => {
    openTabRef.current = tab
    setShowFilters(false)
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("id", id)
      return next
    })
  }

  const closeDetail = () => {
    // Cancel in-flight fetch immediately, then drop ?id= so the drawer unmounts.
    detail.close()
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete("id")
      return next
    })
    setShowFilters(true)
  }

  const handleListBookmark = async (
    e: MouseEvent,
    id: string,
    currentlyBookmarked: boolean
  ) => {
    e.stopPropagation()
    const next = !currentlyBookmarked
    list.patchBookmark(id, next)
    detail.syncBookmark(id, next)
    try {
      if (next) await bookmarkStudentQuestion(id)
      else await unbookmarkStudentQuestion(id)
      toast.success(next ? "Bookmarked" : "Bookmark removed")
    } catch (err) {
      list.patchBookmark(id, currentlyBookmarked)
      detail.syncBookmark(id, currentlyBookmarked)
      toast.error(getLearnerApiErrorMessage(err, "Could not update bookmark"))
    }
  }

  const handleDetailBookmark = async () => {
    const id = detail.detail?.id
    if (!id) return
    const previous = !!detail.detail?.student?.is_bookmarked
    try {
      const next = await detail.toggleBookmark()
      list.patchBookmark(id, next)
      toast.success(next ? "Bookmarked" : "Bookmark removed")
    } catch (err) {
      list.patchBookmark(id, previous)
      toast.error(getLearnerApiErrorMessage(err, "Could not update bookmark"))
    }
  }

  return (
    <div className="questions-page">
      <QuestionsFilterPanel
        open={showFilters}
        draft={draftFilter}
        onChange={handleDraftChange}
        onApply={applyFilters}
        onClose={() => setShowFilters(false)}
        onClear={clearFilters}
      />

      <div className="questions-page__main">
        <QuestionList
          items={displayItems}
          loading={list.loading}
          error={list.error}
          pagination={list.pagination}
          selectedId={detail.detail?.id || detailId}
          keyword={keywordInput}
          hiddenOnMobile={detailOpen}
          onKeywordChange={setKeywordInput}
          onToggleFilters={() => setShowFilters((v) => !v)}
          onOpen={openDetail}
          onToggleBookmark={handleListBookmark}
          onPageChange={handlePageChange}
        />

        {detailOpen && (
          <QuestionDetailPanel
            detail={detail.detail}
            loading={detail.loading}
            error={detail.error}
            activeTab={detail.activeTab}
            savingNote={detail.savingNote}
            savingTag={detail.savingTag}
            onTabChange={detail.setActiveTab}
            onClose={closeDetail}
            onToggleBookmark={handleDetailBookmark}
            onSaveNote={detail.saveNote}
            onAddTag={detail.addTag}
            onRemoveTag={detail.removeTag}
          />
        )}
      </div>
    </div>
  )
}

export default QuestionsPage
